/**
 * PM Message Correction — correction modal with a live word-level diff preview.
 *
 * A correction is sent through the regular send-message endpoint with
 * correct_msg_id / correction_comment params; the server renders the
 * correction bubble (pm/partials/message-body.twig) and the response HTML is
 * appended like any sent message.
 *
 * The diff below mirrors Interpals\Helpers\TextDiff (word tokens with
 * whitespace preserved, LCS, replacement fallback above the cell cap) so the
 * editor preview matches the server-rendered bubble. The tokenizer ranges and
 * invisible-character classes are shared verbatim with the PHP side. Keep the
 * two in sync. Known divergence: the server also enforces a per-render
 * aggregate cell budget across all corrections on the page, so on a page dense
 * with large corrections the sent bubble can degrade to a whole-line
 * replacement even though the preview showed a word-level diff.
 */

(function () {
  var MAX_LCS_CELLS = 250000;

  // Whitespace and ideograph ranges shared verbatim with TextDiff (PHP), so
  // the preview segments text exactly like the server-rendered bubble.
  // Enumerated explicitly rather than via \s / \p{Script=...} because those
  // classes differ between V8 and PCRE (\s over Unicode spaces, script classes
  // over shared CJK punctuation). Ideographic/Thai scripts tokenize per code
  // point; space-delimited scripts (incl. Hangul) tokenize per word.
  var WS = '\\t\\n\\x0B\\f\\r \\u00A0\\u1680\\u2000-\\u200A\\u2028\\u2029\\u202F\\u205F\\u3000\\uFEFF';
  var IDEO = '\\u0E00-\\u0E7F\\u3001-\\u303F\\u3040-\\u30FF\\u31F0-\\u31FF' +
    '\\u3400-\\u4DBF\\u4E00-\\u9FFF\\uF900-\\uFAFF\\uFF66-\\uFF9F\\u{20000}-\\u{2FA1F}';
  var TOKEN_RX = new RegExp('[' + WS + ']+|[' + IDEO + ']|[^' + WS + IDEO + ']+', 'gu');

  function tokenize(text) {
    if (!text) return [];
    return text.match(TOKEN_RX) || [];
  }

  function replacementDiff(oldStr, newStr) {
    var segments = [];
    if (oldStr !== '') segments.push({ text: oldStr, op: 'del' });
    if (newStr !== '') segments.push({ text: newStr, op: 'ins' });
    return segments;
  }

  /**
   * Word-level diff between two strings.
   * Returns ordered segments {text, op} with op one of 'eq' | 'del' | 'ins'.
   */
  window.pmTextDiff = function (oldStr, newStr) {
    oldStr = String(oldStr == null ? '' : oldStr);
    newStr = String(newStr == null ? '' : newStr);

    if (oldStr === newStr) {
      return oldStr === '' ? [] : [{ text: oldStr, op: 'eq' }];
    }

    var oldTokens = tokenize(oldStr);
    var newTokens = tokenize(newStr);
    var n = oldTokens.length;
    var m = newTokens.length;

    if (n === 0 || m === 0 || n * m > MAX_LCS_CELLS) {
      return replacementDiff(oldStr, newStr);
    }

    // LCS length table, flattened row-major into a typed array.
    var width = m + 1;
    var lcs = new Int32Array((n + 1) * width);
    for (var i = 1; i <= n; i++) {
      for (var j = 1; j <= m; j++) {
        if (oldTokens[i - 1] === newTokens[j - 1]) {
          lcs[i * width + j] = lcs[(i - 1) * width + (j - 1)] + 1;
        } else {
          var up = lcs[(i - 1) * width + j];
          var left = lcs[i * width + (j - 1)];
          lcs[i * width + j] = up > left ? up : left;
        }
      }
    }

    // Backtrack (reverse order). Preferring insert on ties makes deletions
    // precede insertions within each replaced run once reversed.
    var reversed = [];
    var bi = n;
    var bj = m;
    while (bi > 0 || bj > 0) {
      if (bi > 0 && bj > 0 && oldTokens[bi - 1] === newTokens[bj - 1]) {
        reversed.push({ op: 'eq', text: oldTokens[bi - 1] });
        bi--;
        bj--;
      } else if (bj > 0 && (bi === 0 || lcs[bi * width + (bj - 1)] >= lcs[(bi - 1) * width + bj])) {
        reversed.push({ op: 'ins', text: newTokens[bj - 1] });
        bj--;
      } else {
        reversed.push({ op: 'del', text: oldTokens[bi - 1] });
        bi--;
      }
    }

    var segments = [];
    for (var k = reversed.length - 1; k >= 0; k--) {
      var part = reversed[k];
      var last = segments[segments.length - 1];
      if (last && last.op === part.op) {
        last.text += part.text;
      } else {
        segments.push({ text: part.text, op: part.op });
      }
    }
    return segments;
  };

  // Segment classes must match pm/partials/message-body.twig so the editor
  // preview looks exactly like the bubble the server will render.
  var DEL_CLASSES = 'line-through decoration-rose-400/70 decoration-[1.5px] text-rose-600 dark:text-rose-300 bg-rose-50 dark:bg-rose-950/40 rounded-[3px] px-0.5';
  var INS_CLASSES = 'no-underline font-medium text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/40 rounded-[3px] px-0.5';

  /**
   * Fill a preview line with diff segments. keepOp selects which changed op
   * this line shows ('del' for the original line, 'ins' for the corrected
   * line); the opposite op is skipped, 'eq' text renders plain.
   */
  function fillDiffLine(el, segments, keepOp) {
    if (!el) return;
    el.textContent = '';
    for (var i = 0; i < segments.length; i++) {
      var seg = segments[i];
      if (seg.op === 'eq') {
        el.appendChild(document.createTextNode(seg.text));
      } else if (seg.op === keepOp) {
        var node = document.createElement(keepOp === 'del' ? 'del' : 'ins');
        node.className = keepOp === 'del' ? DEL_CLASSES : INS_CLASSES;
        node.textContent = seg.text;
        el.appendChild(node);
      }
    }
  }

  /** Open the correction modal for a message (called from the actions menu). */
  window.correctMessage = function (msgId) {
    window.closeMessageActionMenus();
    window.dispatchEvent(new CustomEvent('pm-correct-message', { detail: { msgId: String(msgId) } }));
  };

  /** Alpine component backing #correction-modal (pm/index.twig). */
  window.correctionModal = function () {
    return {
      open: false,
      msgId: '',
      original: '',
      text: '',
      comment: '',
      sending: false,
      error: '',

      init: function () {
        var self = this;
        this.$watch('text', function () {
          self.renderPreview();
        });
      },

      // Invisible characters do not count as a change and Unicode spaces read
      // the same as a plain space; mirrors TextDiff::isVisiblyEqual so the
      // server accepts whatever Send allows.
      get changed() {
        var norm = function (s) {
          return s
            // Stripping variation selectors / fillers is the intent here, so
            // the "misleading character class" they trigger is expected.
            // eslint-disable-next-line no-misleading-character-class
            .replace(/[\p{Cf}\u115F\u1160\u3164\uFFA0\u2800\u180B-\u180D\uFE00-\uFE0F]/gu, '')
            .replace(/[\u{E0100}-\u{E01EF}]/gu, '')
            .replace(/[\u00A0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]/g, ' ')
            .trim();
        };
        var trimmed = norm(this.text);
        return trimmed !== '' && trimmed !== norm(this.original);
      },

      openFromEvent: function (event) {
        this.openModal(event.detail && event.detail.msgId);
      },

      openModal: function (msgId) {
        if (!msgId) return;
        var msgEl = document.getElementById('msg_' + msgId);
        var raw = msgEl ? msgEl.getAttribute('data-msg-raw') : '';
        if (!raw) {
          // Stale DOM (message re-rendered between menu click and handler).
          console.warn('correction: no raw text for msg', msgId);
          return;
        }

        this.msgId = String(msgId);
        this.original = raw;
        this.text = raw;
        this.comment = '';
        this.error = '';
        this.sending = false;
        this.open = true;

        var self = this;
        this.$nextTick(function () {
          self.renderPreview();
          if (window.LucideIcons) window.LucideIcons.initialize(self.$el);
          if (window.matchMedia('(min-width: 640px)').matches && self.$refs.editor) {
            self.$refs.editor.focus();
          }
        });
      },

      close: function () {
        if (this.sending) return;
        this.open = false;
      },

      renderPreview: function () {
        if (!this.open || !this.changed) return;
        // The server trims the sent text before storing/diffing, so diff the
        // trimmed value or edge whitespace would preview as a phantom edit.
        var segments = window.pmTextDiff(this.original, this.text.trim());
        fillDiffLine(this.$refs.previewOriginal, segments, 'del');
        fillDiffLine(this.$refs.previewCorrected, segments, 'ins');
      },

      submit: function () {
        if (!this.changed || this.sending) return;

        var threadId = window.get_active_thread_id();
        if (!$.isNumeric(threadId)) {
          this.error = window.t ? window.t('common.msg_invalid_thread') : 'Invalid conversation';
          return;
        }

        this.sending = true;
        this.error = '';
        var self = this;

        window.pm_ajax_request_active = 1;
        $.ajax({
          type: 'POST',
          url: '/app/messages/send-message',
          data: {
            thread: threadId,
            message: this.text,
            correct_msg_id: this.msgId,
            correction_comment: this.comment
          },
          dataType: 'json'
        }).done(function (response) {
          // Auth/CSRF/onboarding gate: the global ajaxComplete handler
          // navigates on {redirect}, so leave the modal untouched on the way out.
          if (response && typeof response.redirect === 'string' && response.redirect) {
            return;
          }
          if (response && response.success) {
            if (response.body) {
              window.append_msgs(response.body, $('#threads_right'));
            }
            if (window.MessageDisplayManager) {
              window.MessageDisplayManager.updateThreadSidebar(response);
            }
            self.sending = false;
            self.open = false;
          } else {
            self.sending = false;
            self.error = (response && response.error)
              || (window.t ? window.t('common.msg_send_failed') : 'Sending failed');
          }
        }).fail(function () {
          self.sending = false;
          self.error = window.t ? window.t('common.msg_network_error') : 'Network error';
        }).always(function () {
          window.pm_ajax_request_active = 0;
        });
      }
    };
  };
})();
